import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { Badge, Icon } from "@elaniintc/goes-design-system";
import { useQuery } from "@tanstack/react-query";
import axios from "@/utils/axios";
import Tab from "@/components/ui/tab";
import SearchInput from "@/components/common/search-input";
import dayjs from "dayjs";
import { AssociationDetailModal } from "./association-detail-modal";
import type { RequestsVisibility } from "@/queries/confirm-association/confirm-association.api";

/**
 * Tab "Asociaciones" para el detalle de persona en el Dashboard (JER-84, CA-17/CA-18).
 *
 * Endpoint: GET /admin/v1/applicant/{applicantId}/associations
 *
 * Tabla con sub-tabs "Representantes" / "Representaciones" y columnas:
 * Usuario, Estado, Documentación, Fecha de asociación.
 */

interface ApplicantProfile {
  id: string;
  category: string;
  type: string;
  document_type: string;
  document_number: string;
  first_name: string;
  last_name: string | null;
  commercial_name?: string | null;
  email?: string;
  registration_phase?: "PRE_REGISTERED" | "REGISTERED" | "COMPLETED";
}

interface AssociationItem {
  id: string;
  representative?: ApplicantProfile;
  represented?: ApplicantProfile;
  authorization_status: "ACTIVE" | "PENDING" | "REJECTED" | "EXPIRED";
  active: boolean;
  rejection_reason?: string | null;
  authorization_approved_at?: string | null;
  authorization_ended_at?: string | null;
  requests_visibility?: RequestsVisibility | null;
  documents: Array<{ id: string; name: string; url: string }>;
  created_at: string;
  formality_request?: {
    id: string;
    code: string;
  } | null;
}

interface AssociationsResponse {
  data: {
    representatives: AssociationItem[];
    representations: AssociationItem[];
  };
}

const useGetUserAssociations = (applicantId: string) => {
  return useQuery<AssociationsResponse>({
    queryKey: ["user-associations", applicantId],
    queryFn: async () => {
      const response = await axios.get(
        `/admin/v1/applicant/${applicantId}/associations`,
      );
      return response.data;
    },
    enabled: !!applicantId,
  });
};

const getApplicantName = (applicant: ApplicantProfile) => {
  if (applicant.commercial_name) return applicant.commercial_name;
  return `${applicant.first_name} ${applicant.last_name || ""}`.trim();
};

const ITEMS_PER_PAGE = 10;

export const AssociationsTab = () => {
  const params = useParams<{ id: string }>();
  const { data, isLoading, isError } = useGetUserAssociations(params.id ?? "");

  const [activeSubTab, setActiveSubTab] = useState<
    "representatives" | "representations"
  >("representatives");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [detailModal, setDetailModal] = useState<{
    name: string;
    email?: string;
    active: boolean;
    createdAt?: string;
    documents?: Array<{ id: string; name: string; url: string }>;
  } | null>(null);

  const representatives = data?.data?.representatives ?? [];
  const representations = data?.data?.representations ?? [];

  const currentList =
    activeSubTab === "representatives" ? representatives : representations;
  const direction =
    activeSubTab === "representatives" ? "representative" : "represented";

  // Filtrar por búsqueda
  const filteredList = useMemo(() => {
    if (!search.trim()) return currentList;
    const q = search.toLowerCase();
    return currentList.filter((item) => {
      const person =
        direction === "representative" ? item.representative : item.represented;
      if (!person) return false;
      const name = getApplicantName(person).toLowerCase();
      const email = (person.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [currentList, search, direction]);

  // Paginación
  const totalItems = filteredList.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const paginatedList = filteredList.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon
          type="far"
          name="spinner-third"
          className="animate-spin text-xl text-body"
        />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Icon
          type="far"
          name="circle-exclamation"
          className="text-warning text-3xl"
        />
        <p className="text-sm text-body-quiet">
          No se pudieron cargar las asociaciones.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-neutral-quiet rounded-lg overflow-hidden">
      {/* Header con sub-tabs y buscador */}
      <div className="bg-neutral-object px-4 pt-3 flex items-center justify-between">
        <div className="flex">
          <Tab
            active={activeSubTab === "representatives"}
            onClick={() => {
              setActiveSubTab("representatives");
              setCurrentPage(1);
              setSearch("");
            }}
          >
            Representantes
          </Tab>
          <Tab
            active={activeSubTab === "representations"}
            onClick={() => {
              setActiveSubTab("representations");
              setCurrentPage(1);
              setSearch("");
            }}
          >
            Representaciones
          </Tab>
        </div>
        <div className="w-56">
          <SearchInput
            placeholder="Buscar..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            variant="xs"
          />
        </div>
      </div>

      {/* Tabla */}
      <table className="w-full">
        <thead>
          <tr className="border-b border-neutral-quiet bg-neutral-background">
            <th className="text-left text-xs font-semibold text-body-loud uppercase tracking-wide px-4 py-3">
              Usuario
            </th>
            <th className="text-left text-xs font-semibold text-body-loud uppercase tracking-wide px-4 py-3">
              Estado
            </th>
            <th className="text-left text-xs font-semibold text-body-loud uppercase tracking-wide px-4 py-3">
              Documentación
            </th>
            <th className="text-left text-xs font-semibold text-body-loud uppercase tracking-wide px-4 py-3">
              Fecha de asociación
            </th>
          </tr>
        </thead>
        <tbody>
          {paginatedList.length === 0 ? (
            <tr>
              <td
                colSpan={4}
                className="text-center py-12 text-body-quiet text-sm"
              >
                No se encontraron asociaciones.
              </td>
            </tr>
          ) : (
            paginatedList.map((item) => {
              const person =
                direction === "representative"
                  ? item.representative
                  : item.represented;
              if (!person) return null;

              const name = getApplicantName(person);
              const email = person.email || "";
              const docCount = item.documents?.length ?? 0;
              const date = item.created_at
                ? dayjs(item.created_at).format("MMM DD, YYYY | hh:mm A")
                : "";
              const documentsPlural =
                docCount === 1 ? "documento" : "documentos";
              return (
                <tr
                  key={item.id}
                  className="border-b border-neutral-quiet transition-colors"
                >
                  {/* Usuario */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div>
                        <p className="text-sm font-medium text-body-loud">
                          {name}
                        </p>
                        <p className="text-xs text-body">{email}</p>
                      </div>
                      <button
                        type="button"
                        className="size-8 flex items-center justify-center rounded-md border border-neutral-quiet hover:bg-neutral-background cursor-pointer transition-colors"
                        onClick={() =>
                          setDetailModal({
                            name,
                            email,
                            active: item.active,
                            createdAt: item.created_at,
                            documents: item.documents,
                          })
                        }
                      >
                        <Icon
                          type="far"
                          name="arrow-up-right"
                          className="text-xs text-body"
                        />
                      </button>
                    </div>
                  </td>

                  {/* Estado */}
                  <td className="px-4 py-3">
                    <Badge
                      size="xs"
                      variant={item.active ? "success" : "neutral"}
                    >
                      {item.active ? "Activo" : "Inactivo"}
                    </Badge>
                  </td>

                  {/* Documentación */}
                  <td className="px-4 py-3">
                    <Badge
                      size="xs"
                      variant={docCount > 0 ? "info" : "neutral"}
                    >
                      <Icon
                        type="fas"
                        name="file"
                        className={
                          docCount > 0 ? "text-info-quiet" : "text-body-quiet"
                        }
                      />
                      {docCount > 0
                        ? `${docCount} ${documentsPlural}`
                        : "Sin documentos"}
                    </Badge>
                  </td>

                  {/* Fecha */}
                  <td className="px-4 py-3 text-sm text-body">{date}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* Paginación */}
      {totalItems > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-quiet">
          <p className="text-xs text-body-quiet">
            Mostrando{" "}
            <span className="font-semibold text-body-loud">
              {Math.min(totalItems, ITEMS_PER_PAGE)}
            </span>{" "}
            de{" "}
            <span className="font-semibold text-body-loud">{totalItems}</span>{" "}
            resultados
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="size-8 flex items-center justify-center rounded border border-neutral-quiet hover:bg-neutral-background disabled:opacity-50"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                <Icon type="far" name="chevron-left" className="text-xs" />
              </button>
              <button
                type="button"
                className="size-8 flex items-center justify-center rounded border border-neutral-quiet hover:bg-neutral-background disabled:opacity-50"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                <Icon type="far" name="chevron-right" className="text-xs" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal de detalle */}
      <AssociationDetailModal
        isOpen={!!detailModal}
        onClose={() => setDetailModal(null)}
        variant={
          activeSubTab === "representatives"
            ? "representative"
            : "representation"
        }
        data={detailModal}
      />
    </div>
  );
};
